// RITA-1599 (Fund 61) — Hinweis auf der Berater-Seite, wenn das Speichern die geltende Vorlage ändert.
//
// Vor dem Speichern merkt sich die Seite die Vorlagenwahl, wie die Karte „Vertragserstellung“ sie zuletzt geladen hat
// (Apollo-Zwischenspeicher, dieselbe Abfrage mit allen drei Arten; die Karte ist auf jedem Reiter eingehängt). Nach
// erfolgreichem Speichern fragt der Hook die Wahl neu beim Server ab und vergleicht (services/vorlagenwechsel.ts).
// Nur für Super-Admins: Nur sie dürfen die Vorlagenwahl abfragen (Server, getContractCreation), und nur bei ihnen
// steht die Karte. Fehlt der alte Stand oder scheitert die neue Abfrage, gibt es keinen Hinweis – ohne verlässlichen
// Vergleich wäre er geraten; die Karte zeigt ihren eigenen Ladefehler. Eine ältere Antwort (zweites Speichern)
// überschreibt keinen neueren Stand. Der Vergleich ist an den Berater gebunden, der beim Speichern offen war
// (Kollegenreview !806): Die Seite bleibt beim Wechsel auf einen anderen Berater eingehängt, und die Speicherantwort
// kann erst danach eintreffen – dann gilt sie nicht mehr, auch nicht nach A → B → A. Ohne Apollo-Anbindung (etwa in
// älteren Tests der Seite, die Abfragen einzeln nachbilden) bleibt der Hinweis aus, statt die Seite abbrechen zu lassen.
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getApolloContext } from '@apollo/client';
import { getCurrentUser } from '../../../context/CommunicationProvider/authUser';
import { istSuperAdmin } from '../../../utils/istSuperAdmin';
import {
  GET_VERTRAGSAUSWAHL,
  Vertragsauswahl,
  VertragsauswahlAntwort,
  VertragsauswahlVariablen,
} from '../api/vertragserstellung';
import { ALLE_ARTEN } from '../services/vorgang';
import { VorlagenwechselHinweis, vorlagenwechselHinweis } from '../services/vorlagenwechsel';

/** Was die Seite beim Speichern festhält: die Vorlagenwahl vorher und die geänderten Auslöser-Felder. */
export interface Vorlagenvergleich {
  vorher: Vertragsauswahl;
  ausloeser: string[];
  /** Berater, für den gespeichert wird, und seine Generation auf dieser Seite (jeder Wechsel zählt hoch). */
  agentId: number;
  generation: number;
}

export default function useVorlagenwechsel(agentId: number) {
  const { client } = useContext(getApolloContext());
  const [berechtigt] = useState(() => istSuperAdmin(getCurrentUser()));
  const [hinweis, setHinweis] = useState<VorlagenwechselHinweis | null>(null);
  const laufRef = useRef(0);
  // Generation des Beraters auf dieser Seite: Jeder Wechsel (auch zurück zum selben Berater) zählt hoch. Ein beim
  // Speichern gemerkter Vergleich gilt nur in seiner Generation – sonst landete der Hinweis des vorherigen Beraters
  // auf dem gerade geöffneten (Kollegenreview !806).
  const generationRef = useRef(0);

  // Ein anderer Berater (gleiche Seite, andere Nummer): alter Hinweis, gemerkter Vergleich und laufende Abfrage
  // gelten nicht mehr.
  useEffect(() => {
    generationRef.current += 1;
    laufRef.current += 1;
    setHinweis(null);
  }, [agentId]);

  const variablen: VertragsauswahlVariablen = { agent_id: agentId, kinds: ALLE_ARTEN };

  /**
   * Beim Speichern aufrufen: null, wenn kein Hinweis möglich ist (keine Berechtigung, kein Auslöser, kein alter Stand).
   * Bekannte Grenze (Abschlussreview B5): Speichert jemand ein zweites Mal, bevor die Nachabfrage des ersten Speicherns
   * geantwortet hat, ist „vorher“ noch der Stand vor dem ersten Speichern – der Hinweis vergleicht dann über beide
   * Änderungen hinweg.
   */
  const merken = (ausloeser: string[]): Vorlagenvergleich | null => {
    if (!client || !berechtigt || agentId <= 0 || ausloeser.length === 0) return null;
    try {
      const daten = client.readQuery<VertragsauswahlAntwort, VertragsauswahlVariablen>({
        query: GET_VERTRAGSAUSWAHL,
        variables: variablen,
      });
      const vorher = daten?.getContractCreation;
      return vorher && vorher.agent_id === agentId
        ? { vorher, ausloeser, agentId, generation: generationRef.current }
        : null;
    } catch {
      return null;
    }
  };

  /** Nach erfolgreichem Speichern: neue Wahl laden, vergleichen und gegebenenfalls den Hinweis zeigen. */
  const nachSpeichern = async (vergleich: Vorlagenvergleich | null): Promise<void> => {
    if (!vergleich || !client) return;
    // Zwischen Speichern und Antwort auf einen anderen Berater gewechselt: Der Vergleich gehört nicht mehr zur Seite.
    if (vergleich.generation !== generationRef.current) return;
    laufRef.current += 1;
    const lauf = laufRef.current;
    try {
      const { data } = await client.query<VertragsauswahlAntwort, VertragsauswahlVariablen>({
        query: GET_VERTRAGSAUSWAHL,
        variables: { agent_id: vergleich.agentId, kinds: ALLE_ARTEN },
        fetchPolicy: 'network-only',
        // Jedes Speichern braucht seine eigene Antwort: Läuft noch die Abfrage eines früheren Speicherns, würde Apollo
        // sie sonst teilen und der Vergleich nähme den Stand vor diesem Speichern.
        context: { queryDeduplication: false },
      });
      const nachher = data?.getContractCreation;
      if (
        lauf !== laufRef.current ||
        vergleich.generation !== generationRef.current ||
        !nachher ||
        nachher.agent_id !== vergleich.agentId
      )
        return;
      const neu = vorlagenwechselHinweis(vergleich.vorher, nachher, vergleich.ausloeser);
      // Ohne Folgen bleibt ein schon angezeigter Hinweis stehen; er gilt weiter, bis man ihn schließt.
      if (neu) setHinweis(neu);
    } catch {
      // Kein Hinweis ohne verlässlichen Vergleich (siehe Kopf).
    }
  };

  const schliessen = useCallback(() => setHinweis(null), []);

  return { hinweis, schliessen, merken, nachSpeichern };
}
